import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Grid, Stack, Typography } from '@mui/material';
import { showSnackbar } from '../api/snackbarSlice';
import B2CCustomerForm from './form/B2CCustomerForm';

const initialData = {
  name: '',
  company_name: '',
  company_name_in_local: '',
  email: '',
  address1: '',
  address2: '',
  address3: '',
  city: '',
  country_code: '',
  state: '',
  postal_code: '',
  phone: '',
  registration_type: '',
  registration_number: '',
  tin: '',
  sst_registration_number: '-',
};

export default function RequestEInvoiceCustomerForm() {
  const { businessId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [invoiceData, setInvoiceData] = useState(null);
  const [apiErrors, setApiErrors] = useState({});

  useEffect(() => {
    if (location.state && location.state.data) {
      setInvoiceData(location.state.data);
    } else {
      navigate(`/request-e-invoice/${businessId}`, { state: {}, replace: true });
    }
  }, [location.state, navigate, businessId, dispatch]);

  const handleSubmit = async (data) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/invoices/${invoiceData.document_number}/consolidate`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            customerInfo: {
              name: data.name,
              company_name: data.company_name,
              company_name_in_local: data.company_name_in_local,
              email: data.email,
              address1: data.address1,
              address2: data.address2,
              address3: data.address3,
              city: data.city,
              country_code: data.country_code.key,
              state: data.state.key || data.state,
              postal_code: data.postal_code,
              phone: data.phone,
              registration_type: data.registration_type.value,
              registration_number: data.registration_number,
              tin: data.tin,
              sst_registration_number: data.sst_registration_number,
            },
            businessId,
          }),
        },
      );

      if (!response.ok) {
        if (response.status === 400) {
          const errorData = await response.json();
          console.error('Error data:', errorData);
          setApiErrors(errorData.errors || {});
        } else {
          navigate(`/request-e-invoice/${businessId}/error`, { state: {}, replace: true });
        }
      } else {
        dispatch(
          showSnackbar({
            message:
              'E-Invoice Request Received! You will receive an email with Invoice PDF once LHDN approves the Invoice.',
            severity: 'success',
          }),
        );
        navigate(`/request-e-invoice/${businessId}`, { state: {}, replace: true });
      }
    } catch (error) {
      console.error('Error:', error);
      dispatch(showSnackbar({ message: 'An error occurred while updating the invoice.', severity: 'error' }));
    }
  };

  const handleCancel = () => {
    navigate(`/request-e-invoice/${businessId}`, { state: {}, replace: true });
  };

  if (!invoiceData) {
    return null;
  }

  return (
    <Stack
      direction="column"
      spacing={4}
      sx={{
        p: 4,
        borderRadius: '8px',
      }}
    >
      <Typography variant="h5" gutterBottom fontWeight="bold">
        Invoice Details
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 6 }}>
          <Typography variant="body1">
            <strong>Document Number:</strong> {invoiceData.document_number}
          </Typography>
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Typography variant="body1">
            <strong>Document Date:</strong> {new Date(invoiceData.document_date).toLocaleDateString('en-GB')}
          </Typography>
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Typography variant="body1">
            <strong>Total Tax Amount:</strong> {invoiceData.document_currency_code}{' '}
            {invoiceData.total_tax_amount.toFixed(2)}
          </Typography>
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Typography variant="body1">
            <strong>Grand Total:</strong> {invoiceData.document_currency_code} {invoiceData.grand_total.toFixed(2)}
          </Typography>
        </Grid>
      </Grid>

      <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
        Buyer Details
      </Typography>

      <B2CCustomerForm
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        initialData={initialData}
        apiErrors={apiErrors}
      />
    </Stack>
  );
}
